import React from 'preact/compat';
import { useMemo } from 'preact/hooks';

import Typography from '@mui/material/Typography';
import Box from '@mui/material/Box';
import Stack from '@mui/material/Stack';

import LauncherFeatureStackCategoryTitle from './LauncherFeatureStackCategoryTitle';
import LauncherFeatureButton from './LauncherFeatureButton';
import { filterFeatures } from '../../state/utils.js'
import colors from '../colors';

const LauncherFeatureStack = ({
    features, selectedFeatures, onClick, doShowServices, featuresFilter, title, showEmpty, doShowSupportedFeatures,
    largeTitle
}) => {
    const searchFilteredFeatures = useMemo(() => filterFeatures(features, featuresFilter),
        [features, featuresFilter]);

    const filteredFeatures = useMemo(() =>
        doShowSupportedFeatures ? searchFilteredFeatures.filter(f => f.gdkTested || f.gcnTested) : searchFilteredFeatures,
        [searchFilteredFeatures, doShowSupportedFeatures]);

    if (filteredFeatures.length === 0 && !showEmpty) {
        return <Box> </Box>;
    }

    return <Stack
        sx={{
            marginLeft: '24px',
            marginRight: '24px',
            marginTop: largeTitle ? '32px' : '56px',
            '& .MuiButton-root': {
                border: 'none',
                textTransform: 'none',
                justifyContent: 'start',
                display: 'flex',
                alignItems: 'start',
                padding: '8px 8px 6px',
                margin: '3px 0',
                color: colors.primaryText,
                borderRadius: '8px',
                fontSize: '16px',
                fontWeight: 600,
                '& svg': {
                    width: '20px',
                    height: '20px',
                    stroke: colors.inputBorder
                },
                '&.MuiButton-contained': {
                    backgroundColor: colors.featureBackground,
                    border: 'none',
                    color: colors.primaryTextInverted,
                    '& .MuiSvgIcon-root': {
                        fill: colors.option
                    },
                    '& .description': {
                        color: colors.inputText,
                    }
                },
                '&:hover': {
                    border: 'none'
                },
                '& .MuiSvgIcon-root': {
                    marginRight: 3,
                    fill: colors.featureBackground
                },
                '& .title': {
                    fontWeight: 600
                },
                '& .description': {
                    fontSize: '12px',
                    color: '#AAAAAA',
                    textAlign: 'left'
                },
                '& .MuiLink-root': {
                    backgroundColor: colors.action,
                    borderRadius: '2px',
                    display: 'flex',
                    '& .MuiSvgIcon-root': {
                        fill: colors.primaryText,
                        margin: 0,
                        height: '18px',
                        width: '18px'
                    },
                    '&:hover': {
                        backgroundColor: colors.actionHover
                    }
                },
            },
            '& .tag': {
                fontSize: '10px',
                fontWeight: 600,
                borderRadius: '4px',
                borderWidth: '1px',
                borderStyle: 'solid',
                padding: '2px 6px 0',
                marginLeft: '8px',
                '&.service': {
                    borderColor: colors.option
                },
                '&.community': {
                    borderColor: colors.communityTag
                },
                '&.preview': {
                    borderColor: colors.previewTag
                }
            },
            '& .empty-info': {
                textAlign: 'center',
                width: '100%',
                padding: '4px 8px',
                color: colors.primaryText,
                fontSize: '16px',
                fontWeight: 600
            }
        }}
    >

        {title &&
            <LauncherFeatureStackCategoryTitle title={title} large={largeTitle} />
        }
        {filteredFeatures.length === 0 &&
            <Typography className="empty-info"> Nothing Selected </Typography>
        }
        {filteredFeatures.map(feature =>
            <LauncherFeatureButton value={feature.name} title={feature.title} key={feature.name}
                selected={selectedFeatures[feature.name]}
                description={feature.description}
                onClick={onClick}
                isCommunity={feature.community} isPreview={feature.preview} isService={doShowServices}
                documentationLink={
                    doShowServices && feature.documentationModuleName &&
                    `/gdk/modules/#${feature.documentationModuleName}`
                }
            />
        )}
    </Stack>
};

export default LauncherFeatureStack;
